import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Briefcase,
  Moon,
  PhoneCall,
  Sun,
} from "lucide-react";
import { COMPANIES, HUB_STATS } from "@/data/investments";
import { Button } from "@/components/ui/button";
import BlueprintBackground from "@/components/BlueprintBackground";
import LeadModal from "@/components/LeadModal";

const CONSULT_PARCEL = {
  id: "expert-consult",
  name: "Expert Consultation",
  zone: "Dholera SIR",
  zoneLabel: "Investment Hub",
  sector: "—",
  plotSqYd: 0,
  pricePerSqYd: 0,
  type: "Advisory",
};

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

function CompanyLogo({ name, domain, testid }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span
        className="flex h-full w-full items-center justify-center bg-secondary font-display text-sm font-extrabold tracking-tight"
        data-testid={testid}
      >
        {name
          .split(" ")
          .map((w) => w[0])
          .slice(0, 2)
          .join("")}
      </span>
    );
  }
  return (
    <img
      src={`https://logo.clearbit.com/${domain}?size=128`}
      alt={`${name} logo`}
      onError={() => setFailed(true)}
      className="h-9 w-9 object-contain"
      loading="lazy"
      data-testid={testid}
    />
  );
}

const WHY_POINTS = [
  {
    num: "01",
    title: "Employment gravity",
    body: "Every anchor plant pulls four to five times its headcount in indirect jobs — suppliers, contractors, services. That workforce needs housing within commuting distance of the fab, the park and the plants.",
  },
  {
    num: "02",
    title: "Infrastructure follow-through",
    body: "Roads, power and water arrive with the factory, not after it. Corporate capex is the most reliable leading indicator that a region's public infrastructure timeline will hold.",
  },
  {
    num: "03",
    title: "Exit liquidity",
    body: "Deep tenant and workforce demand is what turns a paper gain into a bankable one. Resale and rental depth near anchor employers is what separates plotted wealth from plotted paper.",
  },
];

const HELP_POINTS = [
  "Parcels mapped against anchor projects and commute sheds — not brochures",
  "Title-screened, RERA-verified inventory only; no disputed or unplotted land",
  "NRI desk operating across Gulf, UK and US time zones with virtual walkthroughs",
];

export default function InvestmentHub({ theme, onToggleTheme }) {
  const [lead, setLead] = useState(null);

  return (
    <div
      className="relative min-h-screen bg-background font-sans text-foreground antialiased"
      data-testid="investment-hub"
    >
      <BlueprintBackground />
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-10">
          <Link
            to="/"
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
            data-testid="hub-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Marketplace
          </Link>
          <p className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground sm:block">
            Investment Hub
          </p>
          <button
            onClick={onToggleTheme}
            className="flex h-9 w-9 items-center justify-center border border-border transition-colors duration-200 hover:bg-secondary"
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-12 md:px-10 md:py-20">
        <motion.p
          {...fadeUp(0)}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent"
        >
          Investment Hub — Anchor Demand
        </motion.p>
        <motion.h1
          {...fadeUp(0.08)}
          className="mt-5 max-w-4xl font-display text-4xl font-medium uppercase leading-[1.02] tracking-tight md:text-6xl"
          data-testid="hub-title"
        >
          Companies invested in{" "}
          <span className="text-accent">Dholera Smart City</span>
        </motion.h1>
        <motion.p
          {...fadeUp(0.16)}
          className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base"
        >
          Land doesn't appreciate because of master plans — it appreciates
          because employers arrive. These are the ten anchor commitments
          converting Dholera's blueprints into payrolls, and what each one
          means for your parcel.
        </motion.p>

        <motion.div
          {...fadeUp(0.24)}
          className="mt-12 grid grid-cols-2 gap-px border border-border bg-border md:grid-cols-4"
          data-testid="hub-stats"
        >
          {HUB_STATS.map((s) => (
            <div key={s.label} className="bg-background p-5 md:p-6">
              <p className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
                {s.value}
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>

        <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="companies-grid">
          {COMPANIES.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 2) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col border border-border bg-card p-6"
              data-testid={`company-card-${c.id}`}
            >
              <div className="flex items-start gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-border bg-white p-2">
                  <CompanyLogo name={c.name} domain={c.domain} testid={`company-logo-${c.id}`} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-xl font-medium tracking-tight">
                    {c.name}
                  </h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
                    {c.category}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-end justify-between gap-4 border-y border-border py-4">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground">
                    Committed Investment
                  </p>
                  <p
                    className="mt-1 font-display text-3xl font-extrabold tracking-tight"
                    data-testid={`company-investment-${c.id}`}
                  >
                    {c.investment}
                  </p>
                </div>
                <span className="shrink-0 border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  {c.timeline}
                </span>
              </div>

              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Why it matters for NRIs
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {c.nriImpact}
              </p>

              <div className="mt-auto flex items-center gap-2 pt-5 font-mono text-[11px] text-muted-foreground">
                <span className="mt-5 flex w-full items-center gap-2 border-t border-border pt-4">
                  <Briefcase className="h-3.5 w-3.5 text-accent" />
                  {c.jobs}
                </span>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.section
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-24"
          data-testid="why-matters-section"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
            The Pattern
          </p>
          <h2 className="mt-4 font-display text-3xl font-medium uppercase tracking-tight md:text-5xl">
            Why company investments matter
          </h2>
          <div className="mt-12 border-t border-border">
            {WHY_POINTS.map((w) => (
              <div
                key={w.num}
                className="grid gap-4 border-b border-border py-8 md:grid-cols-12 md:py-10"
              >
                <div className="font-display text-5xl font-light leading-none md:col-span-3">
                  <span className="text-outline-faint">{w.num}</span>
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-display text-lg font-medium uppercase tracking-tight md:text-xl">
                    {w.title}
                  </h3>
                </div>
                <p className="max-w-xl text-sm leading-relaxed text-muted-foreground md:col-span-5 md:text-base">
                  {w.body}
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-24 border border-border bg-card"
          data-testid="how-we-help-section"
        >
          <div className="grid lg:grid-cols-12">
            <div className="p-8 md:p-12 lg:col-span-7">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
                The Desk
              </p>
              <h2 className="mt-4 font-display text-3xl font-medium uppercase tracking-tight md:text-4xl">
                How Dholera NextEra helps buyers
              </h2>
              <div className="mt-8 space-y-4">
                {HELP_POINTS.map((h, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="font-mono text-xs font-bold text-accent">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-sm leading-relaxed text-muted-foreground md:text-base">
                      {h}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-center border-t border-border bg-secondary/40 p-8 md:p-12 lg:col-span-5 lg:border-l lg:border-t-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Free 20-minute mandate call
              </p>
              <p className="mt-3 font-display text-2xl font-medium tracking-tight">
                Talk to the desk before you shortlist a single plot.
              </p>
              <Button
                onClick={() => setLead({ property: CONSULT_PARCEL, mode: "interest" })}
                className="mt-8 h-14 w-full rounded-none font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="consult-experts-btn"
              >
                <PhoneCall className="mr-2 h-4 w-4" />
                Consult with Our Experts
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                No obligation · NRI-friendly slots
              </p>
            </div>
          </div>
        </motion.section>

        <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/70">
          Investment figures and timelines are illustrative mock data · Logos
          belong to their respective owners
        </p>
      </main>

      <LeadModal lead={lead} onClose={() => setLead(null)} />
    </div>
  );
}
